// Clean decklist layout. When `meta` (name → DuckDB card row) is passed,
// we bucket the mainboard by real primary type (Creatures, Planeswalkers,
// Instants, Sorceries, Artifacts, Enchantments, Battles, Lands). Without
// meta we fall back to a lands-vs-rest heuristic so the component still
// renders during the brief window before the batch lookup resolves.

import type React from "react";
import type { CardMeta, Deck, DeckEntry } from "../lib/api";
import CardHover from "./CardHover";
import { cn } from "../lib/cn";
import { deckCost, formatCost } from "../lib/deck-price";
import ManaCost from "./ManaCost";
import SetSymbol from "./SetSymbol";

interface Props {
  deck: Deck;
  meta?: Record<string, CardMeta>;
  /** Slot rendered at the bottom of the right column, under the sideboard.
   *  Used by DeckDetail to place the "Explore deck" CTA next to the deck
   *  lists without stealing screen real estate from the header. */
  sideboardFooter?: React.ReactNode;
}

function sum(entries: DeckEntry[]): number {
  return entries.reduce((s, e) => s + e.count, 0);
}

const BASIC_LANDS = new Set([
  "Plains", "Island", "Swamp", "Mountain", "Forest", "Wastes",
  "Snow-Covered Plains", "Snow-Covered Island", "Snow-Covered Swamp",
  "Snow-Covered Mountain", "Snow-Covered Forest",
]);

function looksLikeLandHeuristic(name: string): boolean {
  if (BASIC_LANDS.has(name)) return true;
  return /\b(Village|Floodplain|Slopes|Marshes|Glade|Town|Caves?|Lagoon|Cliffdwelling|Sewers?|Orchard|Quarry|Concourse|Passageway|Alleyway|Underground|Vista)\b$/i.test(
    name,
  );
}

// Ordering matters — we pick the first type in this list that the card
// has. "Tribal" (legacy) is folded into Enchantment since it barely exists
// in Standard. Battle sits between Artifact and Land in the reading order
// players expect.
const TYPE_ORDER = [
  "Creature",
  "Planeswalker",
  "Instant",
  "Sorcery",
  "Artifact",
  "Enchantment",
  "Battle",
  "Land",
] as const;
type BucketTitle = typeof TYPE_ORDER[number] | "Other";

function bucketFor(name: string, meta: Record<string, CardMeta> | undefined): BucketTitle {
  if (meta) {
    const m = meta[name.toLowerCase()];
    if (m) {
      for (const t of TYPE_ORDER) {
        if (m.types.includes(t)) return t;
      }
      // Double-faced / layout oddities (MDFCs, Sagas, etc.) that didn't
      // match the primary list — fall through to type_line substring as a
      // last ditch before "Other".
      const tl = m.type_line || "";
      for (const t of TYPE_ORDER) {
        if (tl.includes(t)) return t;
      }
      return "Other";
    }
  }
  if (looksLikeLandHeuristic(name)) return "Land";
  return "Other";
}

interface Group {
  title: string;
  entries: DeckEntry[];
  cmcTotal?: number;
}

function pluralize(title: string): string {
  if (title === "Other") return "Other";
  if (title === "Sorcery") return "Sorceries";
  return title + "s";
}

function groupMainboard(
  entries: DeckEntry[],
  meta: Record<string, CardMeta> | undefined,
): Group[] {
  const buckets = new Map<BucketTitle, DeckEntry[]>();
  for (const e of entries) {
    const b = bucketFor(e.name, meta);
    const arr = buckets.get(b) ?? [];
    arr.push(e);
    buckets.set(b, arr);
  }
  const ordered: BucketTitle[] = [...TYPE_ORDER, "Other"];
  const groups: Group[] = [];
  // Sort within a bucket: by CMC asc (lands ignore cmc), then by count desc,
  // then by name asc. Matches the reading order on a physical deck list.
  const sortFn = (bucket: BucketTitle) => (a: DeckEntry, b: DeckEntry) => {
    if (bucket !== "Land" && meta) {
      const ca = meta[a.name.toLowerCase()]?.cmc ?? 99;
      const cb = meta[b.name.toLowerCase()]?.cmc ?? 99;
      if (ca !== cb) return ca - cb;
    }
    return b.count - a.count || a.name.localeCompare(b.name);
  };
  for (const bucket of ordered) {
    const arr = buckets.get(bucket);
    if (!arr || arr.length === 0) continue;
    arr.sort(sortFn(bucket));
    groups.push({ title: pluralize(bucket), entries: arr });
  }
  return groups;
}

function EntryRow({
  entry,
  meta,
}: {
  entry: DeckEntry;
  meta?: Record<string, CardMeta>;
}) {
  const m = meta?.[entry.name.toLowerCase()];
  const copyPrice = m && typeof m.price_usd === "number" ? m.price_usd : null;
  const linePrice = copyPrice != null ? copyPrice * entry.count : null;
  // Row layout kept minimal: count / name / mana pips / set / price.
  // Each column is `auto` so empty data collapses instead of leaving
  // blank vertical bars in the table.
  return (
    <div className="grid grid-cols-[2rem_1fr_auto_auto_auto] items-center gap-3 py-1 font-mono text-[13px] tabular-nums">
      <div className="text-right text-fg-muted leading-none">{entry.count}</div>
      <div className="truncate leading-none">
        <CardHover name={entry.name}>{entry.name}</CardHover>
      </div>
      <div className="flex items-center justify-end min-w-[3.25rem]">
        <ManaCost cost={m?.mana_cost} />
      </div>
      <SetSymbol code={entry.set ?? null} />
      <div
        className="text-right text-[11px] text-fg-subtle/80 min-w-[3rem]"
        title={
          linePrice != null && copyPrice != null && entry.count > 1
            ? `${entry.count}× $${copyPrice.toFixed(2)}`
            : undefined
        }
      >
        {linePrice != null && linePrice > 0 ? `$${linePrice.toFixed(2)}` : ""}
      </div>
    </div>
  );
}

export default function DeckView({ deck, meta, sideboardFooter }: Props) {
  const mainGroups = groupMainboard(deck.mainboard, meta);
  const mainTotal = sum(deck.mainboard);
  const sideTotal = sum(deck.sideboard);
  const mainCost = deckCost(deck.mainboard, meta);
  const sideCost = deckCost(deck.sideboard, meta);

  return (
    <div className="grid lg:grid-cols-[1fr_300px] gap-8 items-start">
      <section>
        <header className="flex items-baseline justify-between mb-3">
          <h2 className="text-sm font-semibold tracking-wide text-fg-muted uppercase">
            Mainboard
          </h2>
          <span className="flex items-baseline gap-3 font-mono text-xs tabular-nums">
            {mainCost && mainCost.usd > 0 && (
              <span
                className="text-fg-subtle"
                title={mainCost.missing > 0 ? `${mainCost.missing} cards missing price data — true total is higher` : "Mainboard total at latest Scryfall price snapshot"}
              >
                {formatCost(mainCost)}
              </span>
            )}
            <span className={cn(mainTotal === 60 ? "text-fg-muted" : "text-warn")}>
              {mainTotal} / 60
            </span>
          </span>
        </header>
        <div className="surface p-4 space-y-5">
          {mainGroups.map((g) => (
            <div key={g.title}>
              <div className="flex items-center justify-between mb-1.5">
                <h3 className="text-[11px] uppercase tracking-wider text-fg-subtle font-medium">
                  {g.title}
                </h3>
                <span className="font-mono text-[11px] text-fg-subtle tabular-nums">
                  {sum(g.entries)}
                </span>
              </div>
              <div>
                {g.entries.map((e, i) => (
                  <EntryRow key={`${e.name}-${i}`} entry={e} meta={meta} />
                ))}
              </div>
            </div>
          ))}
          {mainGroups.length === 0 && (
            <div className="text-xs text-fg-muted">(empty)</div>
          )}
        </div>
      </section>

      <section>
        <header className="flex items-baseline justify-between mb-3">
          <h2 className="text-sm font-semibold tracking-wide text-fg-muted uppercase">
            Sideboard
          </h2>
          <span className="flex items-baseline gap-3 font-mono text-xs tabular-nums">
            {sideCost && sideCost.usd > 0 && (
              <span className="text-fg-subtle" title="Sideboard total">
                {formatCost(sideCost)}
              </span>
            )}
            <span className={cn(sideTotal <= 15 ? "text-fg-muted" : "text-warn")}>
              {sideTotal} / 15
            </span>
          </span>
        </header>
        <div className="surface p-4">
          {deck.sideboard.length === 0 ? (
            <div className="text-xs text-fg-muted">(no sideboard)</div>
          ) : (
            deck.sideboard
              .slice()
              .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name))
              .map((e, i) => <EntryRow key={`${e.name}-${i}`} entry={e} meta={meta} />)
          )}
        </div>
        {sideboardFooter && <div className="mt-5">{sideboardFooter}</div>}
      </section>
    </div>
  );
}
